import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readDocument } from "@/office/word/document";

/** Word speichert den Zeilenabstand in Punkt; die Anzeige teilt den Wert durch 12. */
const EINFACH = 12;
const ANDERTHALB = 18;

export const lektionZeilenabstand: Lesson = {
  id: "zeilenabstand",
  title: "Zeilenabstand und Lesbarkeit",
  summary: "Wie viel Luft zwischen den Zeilen ein Text braucht.",
  durationMinutes: 7,
  learningGoals: [
    "Den Zeilenabstand gezielt einstellen",
    "Typische Vorgaben für Arbeiten und Berichte einordnen",
    "Zeilenabstand von Absatzabstand unterscheiden",
  ],
  steps: [
    {
      id: "zeilenabstand-einstellen",
      title: "Den Zeilenabstand einstellen",
      explanation: [
        "Der Zeilenabstand regelt die Luft zwischen den Zeilen innerhalb eines Absatzes – " +
          "nicht zu verwechseln mit dem Absatzabstand aus der letzten Lektion, der " +
          "zwischen zwei Absätzen wirkt.",
        "Zu wenig Abstand lässt die Zeilen ineinanderlaufen und das Auge findet beim " +
          "Zeilenwechsel schwer zurück. Zu viel zerlegt den Absatz optisch in einzelne " +
          "Streifen. Für Fließtext auf Papier ist der einfache Zeilenabstand meist " +
          "richtig, 1,15 wirkt am Bildschirm oft angenehmer.",
        "Der Schalter dafür sitzt auf der Registerkarte „Start“ in der Gruppe „Absatz“ – " +
          "das Symbol mit den waagerechten Linien und den Pfeilen.",
      ],
      keyPoints: [
        "Zeilenabstand wirkt innerhalb eines Absatzes, Absatzabstand zwischen Absätzen.",
        "Strg + 1 einfach, Strg + 5 anderthalbfach, Strg + 2 doppelt.",
        "Wissenschaftliche Arbeiten verlangen häufig 1,5 – prüfe die Vorgaben.",
      ],
      task: {
        instruction:
          "Stelle einen deiner Fließtextabsätze auf den anderthalbfachen Zeilenabstand um.",
        hint: "Cursor in den Absatz setzen und Strg + 5 drücken.",
        check: async () => {
          const snapshot = await readDocument();
          if (snapshot.filledParagraphs.length === 0) {
            return fail("Im Dokument steht noch kein Text.", "Schreibe zuerst etwas Text.");
          }

          const treffer = snapshot.filledParagraphs.find(
            (absatz) =>
              typeof absatz.format.lineSpacing === "number" &&
              absatz.format.lineSpacing >= ANDERTHALB - 1 &&
              absatz.format.lineSpacing <= ANDERTHALB + 1,
          );

          if (!treffer) {
            const werte = [
              ...new Set(
                snapshot.filledParagraphs
                  .map((absatz) => absatz.format.lineSpacing)
                  .filter((wert): wert is number => typeof wert === "number"),
              ),
            ].map((wert) => `${(wert / EINFACH).toFixed(2).replace(".", ",")}-fach`);

            return fail(
              `Noch kein Absatz mit anderthalbfachem Zeilenabstand. Gefunden: ${
                werte.join(", ") || "keine Angabe"
              }.`,
              "Strg + 5 stellt den anderthalbfachen Abstand ein – nicht zu verwechseln mit Strg + 2 für doppelt.",
            );
          }

          return pass(
            `Passt – „${treffer.text.slice(0, 40)}…“ hat jetzt spürbar mehr Luft.`,
          );
        },
      },
    },
    {
      id: "abstand-vs-zeilenabstand",
      title: "Die beiden Abstände auseinanderhalten",
      explanation: [
        "In der Praxis werden die beiden Einstellungen oft verwechselt, und das führt zu " +
          "seltsam aussehenden Dokumenten: Wer mehr Luft zwischen den Absätzen will und " +
          "stattdessen den Zeilenabstand erhöht, zieht den gesamten Text auseinander.",
        "Merksatz: Der Zeilenabstand gehört zum Lesen einer Zeile, der Absatzabstand zum " +
          "Trennen von Gedanken. Wenn du unsicher bist, blende die Absatzmarken ein – " +
          "dann siehst du sofort, wo ein Absatz endet.",
        "Beides zusammen findest du im Absatzdialog: kleiner Pfeil rechts unten in der " +
          "Gruppe „Absatz“. Dort lassen sich Ausrichtung, Einzüge, Abstand und " +
          "Zeilenabstand in einem Rutsch einstellen – und mit „Als Standard festlegen“ " +
          "auch dauerhaft übernehmen.",
      ],
      keyPoints: [
        "Zeilenabstand = innerhalb des Absatzes. Absatzabstand = zwischen Absätzen.",
        "Der Absatzdialog bündelt alle Absatzeinstellungen an einer Stelle.",
        "Strg + Umschalt + 8 blendet die Absatzmarken ein und aus.",
      ],
    },
  ],
};
